import * as t from 'io-ts';
import {chain, find, uniq} from 'lodash';

import {RActivityIdRef} from './activityModel';
import {
  makeApiSerializableType,
  makeApiType,
  makeDefaultValueType,
  makeEnumRuntimeType,
  RChannelIdRef,
  RCommentUidRef,
  RConversationIdRef,
  RInboxIdRef,
  RLazyTeammateIdRef,
  RMessageUidRef,
  RTagIdRef,
  RTopicIdRef,
} from './apiTypes';
import {
  ConversationSortPropertiesEnum,
  ConversationTeamStatusesEnum,
  RConversationOrdersSortProperty,
} from './conversationCommonTypes';
import {parseConversationSafetyNet, RConversationSafetyNet} from './conversationSafetyNetModel';
import {parseConversationTracker, RConversationTracker} from './conversationTrackerModel';
import {parseDraftSeed, RDraftSeed} from './draftSeedModel';
import {parseDraftSummary, RDraftSummary} from './draftSummaryModel';
import {MessageStatusesEnum, MessageTypesEnum, parseMessageType} from './messageModel';
import {parseConversationPrecog, parseConversationSentPrecog} from './precogModel';
import {parseRecipient, RRecipient} from './recipientModel';
import {parseReminder, RReminder, serializeReminder} from './reminderModel';
import {parseTopic} from './topicModel';

const RTrackersDictionary = t.record(t.string, t.union([RConversationTracker, t.undefined]));
export interface TrackersDictionary extends t.TypeOf<typeof RTrackersDictionary> {}

const ROrdersDictionary = t.record(RConversationOrdersSortProperty, t.Integer);

const RContentUpdatedDatesDictionary = t.intersection([
  t.record(t.string, t.union([t.Integer, t.undefined])),
  t.type({default: t.Integer}),
]);

const RLastComment = t.type({
  uid: RCommentUidRef,
  sentAt: t.Integer,
});

const RLastMessage = t.type({
  uid: RMessageUidRef,
  sentAt: t.Integer,
  status: makeEnumRuntimeType(MessageStatusesEnum),
});

export const RConversationBase = t.readonly(
  t.intersection([
    t.type({
      id: t.Integer,
      url: t.string,
      namespace: t.string,
      numMessages: t.union([t.Integer, t.undefined]),
      teamStatus: makeEnumRuntimeType(ConversationTeamStatusesEnum),
      recipient: t.union([RRecipient, t.undefined]),
      subject: t.string,
      messageType: makeEnumRuntimeType(MessageTypesEnum),
      summary: t.string,
      reminders: t.readonlyArray(RReminder),
      canceledReminders: t.readonlyArray(RReminder),
      topicIds: t.readonlyArray(RTopicIdRef),
      hasAttachments: t.boolean,
      isUnsnoozed: t.boolean,
      seenAt: t.union([t.Integer, t.undefined]),
      updatedAt: t.Integer,
      assigneeId: t.union([RLazyTeammateIdRef, t.undefined]),
      inboxIds: t.readonlyArray(RInboxIdRef),
      channelIds: t.readonlyArray(RChannelIdRef),
      tagIds: t.readonlyArray(RTagIdRef),
      trackers: RTrackersDictionary,
      orders: ROrdersDictionary,
    }),
    t.partial({
      safetyNets: t.readonlyArray(RConversationSafetyNet),
    }),
  ]),
  'conversationBase',
);

export const RConversation = t.readonly(
  t.intersection([
    RConversationBase,
    t.type({
      numPinnedActivities: makeDefaultValueType(t.Integer, 0),
      link: t.string,
      numMessages: t.Integer,
      hasOverriddenSubject: makeDefaultValueType(t.boolean, false),
      cannotReply: t.boolean,
      autoSeenAt: t.union([t.Integer, t.undefined]),
      seenUpdatedAt: t.union([t.Integer, t.undefined]),
      lastActivityAt: t.Integer,
      /** Indicates whether draftCommentUids and draftMessageUids are complete sets. */
      isContentLoaded: t.boolean,
      draftCommentUids: t.readonlyArray(RCommentUidRef),
      draftMessageUids: t.readonlyArray(RMessageUidRef),
      draftSeeds: t.readonlyArray(RDraftSeed),
      contentUpdatedDates: RContentUpdatedDatesDictionary,
      lastComment: t.union([RLastComment, t.undefined]),
      lastMessage: t.union([RLastMessage, t.undefined]),
      lastManualMessage: t.union([RLastMessage, t.undefined]),
      bumpActivityId: t.union([RActivityIdRef, t.undefined]),
      duplicateConversationIds: makeDefaultValueType(t.readonlyArray(t.Integer), []),
      shouldShowDuplicateBanner: t.boolean,
      isGuidedDemo: t.boolean,
      lastDraft: t.union([RDraftSummary, t.undefined]),
      lastCommentDraft: t.union([RDraftSummary, t.undefined]),
    }),
    t.partial({
      /** Batch ID for offline seen actions. */
      offlineSeenId: t.union([t.undefined, t.string]),
      unsavedDraftCommentUids: t.readonlyArray(RCommentUidRef),
      unsavedDraftMessageUids: t.readonlyArray(RMessageUidRef),
    }),
  ]),
  'conversation',
);

/*
 * Types.
 */

export interface ContentUpdatedDates extends t.TypeOf<typeof RContentUpdatedDatesDictionary> {}
export interface ConversationOrdersModel extends t.TypeOf<typeof ROrdersDictionary> {}
export interface ConversationLastComment extends Readonly<t.TypeOf<typeof RLastComment>> {}
/** A message partial that is used to represent the conversation's last message or last manual message. */
export interface ConversationLastMessage extends Readonly<t.TypeOf<typeof RLastMessage>> {}
export interface ConversationBaseModel extends t.TypeOf<typeof RConversationBase> {}
export interface ConversationModel extends t.TypeOf<typeof RConversation> {}
RConversationIdRef.setTargetType(RConversation);

/*
 * IO methods.
 */

export function parseConversation(src: any): ConversationModel {
  const bumpedAt = src.bumped_at;
  const waitingSince = src.waiting_since || bumpedAt;
  const lastActivityDate = src.last_activity_at || bumpedAt;
  const lastMessageOrCommentDate = src.last_comment_or_reply_at || lastActivityDate;

  return {
    id: src.id,
    url: src.url,
    namespace: src.namespace,
    teamStatus: parseConversationStatus(src),
    recipient: (src.contact && parseRecipient(src.contact)) || undefined,
    subject: src.subject,
    numMessages: src.num_messages,
    numPinnedActivities: src.num_pinned_activities || 0,
    messageType: parseMessageType(src.message_type) || MessageTypesEnum.INTERNAL,
    summary: src.summary,
    link: src.link || '',
    reminders: src.reminders && src.reminders.map(parseReminder),
    canceledReminders: (src.canceled_reminders && src.canceled_reminders.map(parseReminder)) || [],
    topicIds: (src.topics && src.topics.map(parseTopic).map((topic: any) => topic.id)) || [],
    hasAttachments: src.has_attachments,
    hasOverriddenSubject: src.has_overridden_subject,
    isUnsnoozed: Boolean(src.unsnoozed),
    cannotReply: Boolean(src.cannot_reply),
    seenAt: Number.isInteger(src.seen_at) ? src.seen_at : undefined,
    autoSeenAt: Number.isInteger(src.auto_seen_at) ? src.auto_seen_at : undefined,
    seenUpdatedAt: Number.isInteger(src.seen_updated_at) ? src.seen_updated_at : undefined,
    lastActivityAt: lastActivityDate,
    updatedAt: src.updated_at,
    isContentLoaded: Boolean(src.timeline && src.draft_comments && src.draft_messages),
    draftCommentUids: Array.isArray(src.draft_comments) ? src.draft_comments.map((c: any) => c.uid) : [],
    draftMessageUids: Array.isArray(src.draft_messages) ? src.draft_messages.map((m: any) => m.uid) : [],
    draftSeeds: Array.isArray(src.other_drafts) ? src.other_drafts.map((s: any) => parseDraftSeed(s)) : [],
    contentUpdatedDates: src.content_updated_dates,

    assigneeId: src.assignee_id || undefined,
    inboxIds: src.inbox_ids,
    channelIds: src.channels && uniq(src.channels),
    tagIds: src.tags && src.tags.map((tag: any) => tag.id),
    lastComment: parseLastComment(src),
    lastManualMessage: parseLastMessage(src.last_manual_message),
    lastMessage: parseLastMessage(src.last_message),
    bumpActivityId: (src.bump_activity && src.bump_activity.id) || undefined,
    duplicateConversationIds: src.duplicate_conversation_ids || [],
    shouldShowDuplicateBanner: Boolean(src.should_show_duplicate_banner),
    safetyNets: src.safety_nets?.map(parseConversationSafetyNet) || undefined,

    trackers: parseTrackers(src.trackers, src.seen_by),
    orders: {
      [ConversationSortPropertiesEnum.BUMPED_AT]: bumpedAt,
      [ConversationSortPropertiesEnum.WAITING_SINCE]: waitingSince,
      [ConversationSortPropertiesEnum.LAST_ACTIVITY_DATE]: lastActivityDate,
      [ConversationSortPropertiesEnum.LAST_MESSAGE_OR_COMMENT_DATE]: lastMessageOrCommentDate,
    },
    isGuidedDemo: Boolean(src.onboarding_demo),
    lastDraft: src.last_draft && parseDraftSummary(src.last_draft),
    lastCommentDraft: src.last_comment_draft && parseDraftSummary(src.last_comment_draft),
  };
}

export function parseConversationPrecogs(src: any) {
  const typingPrecogs = (src.precogs || []).map((precog: any) => parseConversationPrecog(src.id, precog));
  const sentPrecogs = (src.sentPrecogs || []).map((precog: any) =>
    parseConversationSentPrecog(src.id, precog),
  );
  return [...typingPrecogs, ...sentPrecogs];
}

function parseLastComment(src: any): ConversationLastComment | undefined {
  const lastComment = src.last_comment;
  if (!lastComment) {
    return undefined;
  }

  // If the last comment is not part of the provided timeline, return a partial UID.
  const lastCommentActivity = find(src.timeline, (a: any) => a.comment && a.comment.uid === lastComment.uid);
  if (!lastCommentActivity) {
    const {uid, date} = lastComment;
    return {
      uid: `partial_${uid}`,
      sentAt: date,
    };
  }

  // Otherwise, return the actual UID.
  const {uid, date} = lastCommentActivity.comment;
  return {
    uid,
    sentAt: date,
  };
}

function parseLastMessage(srcMessage: any): ConversationLastMessage | undefined {
  if (!srcMessage) {
    return undefined;
  }

  const {uid, date, status} = srcMessage;
  return {
    uid,
    status,
    sentAt: date,
  };
}

export function parseConversationStatus(src: any) {
  switch (src.status) {
    case 'assigned':
    case 'unassigned':
      return ConversationTeamStatusesEnum.OPEN;

    case 'trashed':
      return src.spam ? ConversationTeamStatusesEnum.SPAM : ConversationTeamStatusesEnum.TRASHED;

    default:
      return src.status;
  }
}

function parseTrackersWithConversation(src: any) {
  return parseTrackers(src.trackers, src.seen_by);
}

export function parseTrackers(srcTrackers: any, srcSeenBy: any) {
  return chain(srcTrackers)
    .map((tr) => parseConversationTracker(tr, srcSeenBy))
    .keyBy((i) => Number(i.teammateId))
    .value();
}

export function serializeConversation(conversation: ConversationModel): any {
  return {
    id: conversation.id,
    url: conversation.url,
    namespace: conversation.namespace,
    status: conversation.teamStatus.toString(),
    recipient: conversation.recipient,
    subject: conversation.subject,
    num_messages: conversation.numMessages,
    num_pinned_activities: conversation.numPinnedActivities,
    message_type: conversation.messageType,
    summary: conversation.summary,
    link: conversation.link,
    reminders: conversation.reminders.map(serializeReminder),
    has_attachments: conversation.hasAttachments,
    unsnoozed: conversation.isUnsnoozed,
    seen_at: conversation.seenAt,
    updated_at: conversation.updatedAt,

    assignee_id: conversation.assigneeId,
    inbox_ids: conversation.inboxIds,
    channel_ids: conversation.channelIds,
    tag_ids: conversation.tagIds,
  };
}

/*
 * API type.
 */

export const ApiConversation = makeApiSerializableType(
  'conversation',
  RConversation,
  parseConversation,
  serializeConversation,
);

export const ApiTrackers = makeApiType('trackers', RTrackersDictionary, parseTrackersWithConversation);
